import { onBeforeUnmount, watch, toValue, type MaybeRefOrGetter } from '@/composition/vue/compositionApi'

export const useResizeObserver = (
  target: MaybeRefOrGetter<HTMLElement | undefined | null>,
  callback: ResizeObserverCallback,
) => {
  const resizeObserver = new ResizeObserver(callback)

  const reconnectObserver = (value: HTMLElement | undefined | null) => {
    if (!value) return
    resizeObserver.observe(value)
  }

  const cancelWatcher = watch(
    () => toValue(target),
    (newValue) => reconnectObserver(newValue),
    { immediate: true },
  )

  onBeforeUnmount(() => {
    resizeObserver?.disconnect()
  })

  return () => {
    cancelWatcher()
    resizeObserver?.disconnect()
  }
}

export const useCollectionResizeObserver = (
  target: MaybeRefOrGetter<HTMLElement[]>,
  callback: (el: HTMLElement) => void,
) => {
  const observersMap = new Map<HTMLElement, ResizeObserver>()

  const reconnectObservers = (elements: HTMLElement[]) => {
    const oldEelements = new Set(observersMap.keys())

    elements.forEach((el) => {
      if (observersMap.has(el)) {
        oldEelements.delete(el)
        return
      }
      const observer = new ResizeObserver(() => {
        callback(el)
      })
      observer.observe(el)
      observersMap.set(el, observer)
    })

    oldEelements.forEach((el) => {
      observersMap.get(el)?.disconnect()
      observersMap.delete(el)
    })
  }

  const clearObservers = () => {
    observersMap.forEach((observer) => observer.disconnect())
    observersMap.clear()
  }

  const cancelWatcher = watch(
    () => toValue(target),
    (newValue) => reconnectObservers(newValue),
    { immediate: true },
  )

  onBeforeUnmount(() => {
    clearObservers()
  })

  return () => {
    cancelWatcher()
    clearObservers()
  }
}
